import { type CSSProperties, type ReactNode } from "react";
import { Card, EmptyState } from "../primitives";

/**
 * Shared cockpit drill-in panel chrome (Sprint 5n). Every count tile became a
 * browseable list; this owns the header (title + count + optional action) and
 * the loading / error / empty states so each panel stays a thin data binding.
 */
interface PanelProps {
  title: string;
  count?: number;
  /** Right-aligned header control (e.g. an upload / new button). */
  action?: ReactNode;
  /**
   * Sprint 12a — render `action` on its own row below the title instead of
   * inline. Use in narrow columns (e.g. the 320px right rail) where a title +
   * count + multi-button action would collide on one row. Default `false`
   * keeps the existing single-row layout, so all prior callsites are unaffected.
   */
  actionsBelowTitle?: boolean;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  isEmpty?: boolean;
  empty?: ReactNode;
  /**
   * Sprint 19 — a notice rendered between the header and the body, ABOVE the
   * loading/empty/rows branch so it shows even when scope hid every row. Used
   * for the <ScopeFilteredBanner>.
   */
  banner?: ReactNode;
  /** Optional footer note rendered below the rows (e.g. forward-compat cue). */
  footer?: ReactNode;
  skeletonRows?: number;
  children?: ReactNode;
}

const headingRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginBottom: 8,
};

const headingText: CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  display: "flex",
  alignItems: "baseline",
  gap: 6,
};

const countStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 400,
  color: "var(--color-text-tertiary)",
  fontVariantNumeric: "tabular-nums",
};

export function Panel({
  title,
  count,
  action,
  actionsBelowTitle = false,
  isLoading,
  error,
  onRetry,
  isEmpty,
  empty,
  banner,
  footer,
  skeletonRows = 3,
  children,
}: PanelProps) {
  const titleBlock = (
    <div style={headingText}>
      {title}
      {count !== undefined && <span style={countStyle}>{count}</span>}
    </div>
  );
  return (
    <Card>
      {actionsBelowTitle ? (
        <div style={{ marginBottom: 8 }}>
          {titleBlock}
          {action && <div style={{ paddingTop: 4 }}>{action}</div>}
        </div>
      ) : (
        <div style={headingRow}>
          {titleBlock}
          {action}
        </div>
      )}
      {banner}
      {isLoading ? (
        <PanelSkeleton rows={skeletonRows} />
      ) : error ? (
        <PanelError panel={title} onRetry={onRetry} />
      ) : isEmpty ? (
        empty ?? <EmptyState>Nothing here yet.</EmptyState>
      ) : (
        children
      )}
      {!isLoading && !error && footer}
    </Card>
  );
}

function PanelSkeleton({ rows }: { rows: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "4px 0" }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="kuvio-skeleton"
          style={{
            height: 32,
            borderRadius: "var(--border-radius-sm)",
            background: "var(--color-background-secondary)",
          }}
        />
      ))}
    </div>
  );
}

function PanelError({ panel, onRetry }: { panel: string; onRetry?: () => void }) {
  return (
    <div
      style={{
        fontSize: 13,
        color: "var(--color-text-secondary)",
        padding: "12px 0",
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <i className="ti ti-alert-circle" style={{ fontSize: 14, color: "var(--c-red-600)" }} aria-hidden="true" />
      Couldn't load {panel.toLowerCase()}.
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            border: "none",
            background: "transparent",
            color: "var(--color-text-info)",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 500,
            padding: 0,
            fontFamily: "var(--font-sans)",
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <PanelRow> — one clickable list row with a consistent hover affordance.
//
// Sprint 11 adds an optional column system so list panels (Pages, Tasks,
// Memories, Files, Owned tables, Tasks, Prompts) align their metadata
// columns regardless of label width — a surface chip column stays exactly
// PANEL_COL_SURFACE px whether it says "Spec" or "Meeting-note". Backward
// compatible: a row that passes `children` (no `columns`) renders flat as before.
// ---------------------------------------------------------------------------

/** Canonical column widths (px), exported for reuse across list panels.
 *  Sprint 12a tuned the first three down: the original widths were 3–5× the
 *  chip/label they hold, eating space the title column needs. Empirically the
 *  longest expected labels fit with breathing room at the new values. */
export const PANEL_COL_SURFACE = 70;       // was 100 ("Meeting-note" ≈ 64px)
export const PANEL_COL_VERIFICATION = 140; // was 180 ("Verified Xd ago" ≈ 110px)
export const PANEL_COL_STATUS = 90;        // was 110 ("In-progress" ≈ 75px)
export const PANEL_COL_TIME = 80;          // unchanged
export const PANEL_COL_ICON = 24;          // unchanged

export interface PanelColumn {
  /** Fixed width in px. Mutually exclusive with `flex`. */
  width?: number;
  /** Take the remaining space (the title column). */
  flex?: boolean;
  align?: "left" | "right" | "center";
  content: ReactNode;
  key?: string;
}

interface PanelRowProps {
  onClick?: () => void;
  /** Flat content (legacy). Ignored when `columns` is provided. */
  children?: ReactNode;
  /** Sprint 11 — aligned columns. Fixed-width columns get exact px; flex takes the rest. */
  columns?: PanelColumn[];
  /** Extra controls pinned to the right (e.g. an open icon). */
  trailing?: ReactNode;
  title?: string;
}

const alignToJustify: Record<NonNullable<PanelColumn["align"]>, CSSProperties["justifyContent"]> = {
  left: "flex-start",
  right: "flex-end",
  center: "center",
};

export function PanelRow({ onClick, children, columns, trailing, title }: PanelRowProps) {
  return (
    <div
      onClick={onClick}
      title={title}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
      className="kuvio-panel-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 8px",
        borderRadius: "var(--border-radius-sm)",
        cursor: onClick ? "pointer" : "default",
        fontFamily: "var(--font-sans)",
        minWidth: 0,
      }}
    >
      {columns ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          {columns.map((col, i) => (
            <div
              key={col.key ?? i}
              style={{
                ...(col.flex
                  ? { flex: 1, minWidth: 0 }
                  : { width: col.width, flexShrink: 0 }),
                display: "flex",
                alignItems: "center",
                justifyContent: alignToJustify[col.align ?? "left"],
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {col.content}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          {children}
        </div>
      )}
      {trailing}
    </div>
  );
}
